import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import { pixelMask, TILE_SYMBOLS } from '@/utils/tilePixels';
import { Signal } from '@/models/data/Signal';
import { TileType } from '@/models/TileTypes';
import {
  TileDefinition,
  TileGraphicsDefinition,
  GraphBuildQueueItem,
} from '@/models/tiles/TileDefinition';
import { Side, TileSideIOType } from '@/schemas/tiles';
import { colors } from '@/styles';
import {
  defaultTileGraphics,
  sideGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import {
  getGlobalDirection,
  getLocalSide,
  getNeighborPosition,
} from '@/models/logic/utils';
import { Direction, opposite } from '@/utils/direction';
import { Point } from '@/models/Point';
import { LogicGraph } from '@/models/logic/LogicGraph';
import { ITileManagerModel } from '@/interfaces/models';
import { Rotation } from '@/models/data/Rotation';
import { TileInstance } from '@/models/tiles/TileInstance';

export const andIoSides = {
  [Side.TOP]: { type: TileSideIOType.INPUT, forceConnection: true },
  [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: true },
  [Side.BOTTOM]: { type: TileSideIOType.INPUT, forceConnection: true },
  [Side.LEFT]: { type: TileSideIOType.NONE, forceConnection: false },
};

export interface AndTileState {
  topNodeId?: NodeHandle;
  bottomNodeId?: NodeHandle;
  outNodeId?: NodeHandle;
}

export class AndTile implements TileDefinition<AndTileState> {
  type = TileType.And;

  name = 'And';
  canRotate = true;

  createDefaultState = () => ({});

  buildAndConnectNodes = (data: {
    graph: LogicGraph;
    tileManager: ITileManagerModel;
    instance: TileInstance<AndTileState>;
    sourceNodeId?: NodeHandle;
    sourceTileId?: string;
    entryDirection?: Direction;
  }): GraphBuildQueueItem[] => {
    const { graph, tileManager, instance, sourceNodeId, sourceTileId, entryDirection } =
      data;

    // Initialize state if needed
    if (!instance.state) instance.state = {};
    const state = instance.state;

    if (
      !state.topNodeId ||
      !graph.nodes.has(state.topNodeId) ||
      !state.bottomNodeId ||
      !graph.nodes.has(state.bottomNodeId) ||
      !state.outNodeId ||
      !graph.nodes.has(state.outNodeId)
    ) {
      state.topNodeId = graph.addNode('NOT').id;
      state.bottomNodeId = graph.addNode('NOT').id;
      state.outNodeId = graph.addNode('NOT').id;

      // Connect internal logic: NotTop -> NotOut, NotBottom -> NotOut
      graph.addEdge(state.topNodeId, state.outNodeId);
      graph.addEdge(state.bottomNodeId, state.outNodeId);

      // Set main logicNodeId to the output node
      instance.logicNodeIds = {
        [Side.RIGHT]: state.outNodeId,
        [Side.TOP]: state.topNodeId,
        [Side.BOTTOM]: state.bottomNodeId,
      };
    }

    // Proactively connect to neighbors on input sides
    // This handles cases where the neighbor didn't trigger a connection to us
    const inputSides = [Side.TOP, Side.BOTTOM];
    for (const side of inputSides) {
      const globalDir = getGlobalDirection(side, instance.meta.rotation as Rotation);
      if (globalDir !== null) {
        const neighborPos = getNeighborPosition(
          Point.fromData(instance.location),
          globalDir,
        );
        const neighbor = tileManager.get(neighborPos);

        // TODO: Util for this?
        const neighborSide = side === Side.TOP ? Side.BOTTOM : Side.TOP; // Opposite side of the neighbor
        if (neighbor && neighbor.logicNodeIds?.[neighborSide]) {
          const targetNodeId = side === Side.TOP ? state.topNodeId! : state.bottomNodeId!;

          graph.addEdge(neighbor.logicNodeIds[neighborSide], targetNodeId, {
            sourceTileId: neighbor.id,
            targetTileId: instance.id,
            targetSide: side,
          });
        }
      }
    }

    // Handle incoming connection
    if (sourceNodeId && entryDirection !== undefined) {
      const incomingGlobalDir = opposite(entryDirection);
      const localSide = getLocalSide(
        incomingGlobalDir,
        instance.meta.rotation as Rotation,
      );

      if (localSide === Side.TOP) {
        graph.addEdge(sourceNodeId, state.topNodeId!, {
          sourceTileId,
          targetTileId: instance.id,
          targetSide: Side.TOP,
        });
      } else if (localSide === Side.BOTTOM) {
        graph.addEdge(sourceNodeId, state.bottomNodeId!, {
          sourceTileId,
          targetTileId: instance.id,
          targetSide: Side.BOTTOM,
        });
      }
    }

    // Propagate to neighbors
    const queueItems: GraphBuildQueueItem[] = [];

    // Output is on RIGHT side
    const outSide = Side.RIGHT;
    const globalDir = getGlobalDirection(outSide, instance.meta.rotation as Rotation);
    if (globalDir !== null) {
      const neighborPos = getNeighborPosition(
        Point.fromData(instance.location),
        globalDir,
      );
      const neighbor = tileManager.get(neighborPos);
      if (neighbor) {
        queueItems.push({
          tile: neighbor,
          sourceNodeId: state.outNodeId!,
          sourceTileId: instance.id,
          entryDirection: globalDir,
        });
      }
    }

    return queueItems;
  };

  syncStateIsVisualOnly = true;

  syncState = ({
    instance,
    graph,
  }: {
    instance: TileInstance<AndTileState>;
    graph: LogicGraph;
  }) => {
    const state = instance.state;
    if (!state.topNodeId || !state.bottomNodeId || !state.outNodeId) return;

    const topNode = graph.nodes.get(state.topNodeId);
    const bottomNode = graph.nodes.get(state.bottomNodeId);
    const outNode = graph.nodes.get(state.outNodeId);

    if (topNode) {
      // Input to NOT gate is the signal we want to visualize as input
      const input = graph.getNodeInput(topNode.id);
      instance.meta.inputs[Side.TOP] = input ? Signal.HIGH : Signal.LOW;
    }
    if (bottomNode) {
      const input = graph.getNodeInput(bottomNode.id);
      instance.meta.inputs[Side.BOTTOM] = input ? Signal.HIGH : Signal.LOW;
    }
    if (outNode) {
      const output = outNode.output;
      instance.meta.outputs[Side.RIGHT] = output ? Signal.HIGH : Signal.LOW;
    }
  };

  saveState({
    instance,
    graph,
  }: {
    instance: TileInstance<AndTileState>;
    graph: LogicGraph;
  }) {
    const s = instance.state;
    const getNodeState = (id?: NodeHandle) => {
      if (!id) return null;
      const node = graph.nodes.get(id);
      return node ? { output: node.output, state: node.state } : null;
    };

    return {
      top: getNodeState(s.topNodeId),
      bottom: getNodeState(s.bottomNodeId),
      out: getNodeState(s.outNodeId),
    };
  }

  restoreState({
    instance,
    graph,
    savedState,
  }: {
    instance: TileInstance<AndTileState>;
    graph: LogicGraph;
    savedState: any;
  }) {
    const s = instance.state;
    const restoreNodeState = (id: NodeHandle | undefined, state: any) => {
      if (!id || !state) return;
      const node = graph.nodes.get(id);
      if (node) {
        node.output = state.output;
        if (node.state && state.state) {
          Object.assign(node.state, state.state);
        } else {
          node.state = state.state;
        }
      }
    };

    restoreNodeState(s.topNodeId, savedState.top);
    restoreNodeState(s.bottomNodeId, savedState.bottom);
    restoreNodeState(s.outNodeId, savedState.out);
  }

  graphics = andTileGraphics;

  ioSides = andIoSides;
}

export const andTileGraphics: TileGraphicsDefinition<AndTileState> = {
  type: TileType.And,
  draw: function (g, { meta, connections, context }): void {
    defaultTileGraphics(g);
    sideGraphics(g, andIoSides, colors.signalColor, colors.noSignalColor, meta);
    connectionGraphics(
      g,
      colors.signalColor,
      colors.noSignalColor,
      meta,
      connections,
      andIoSides,
      context,
    );

    const color =
      meta.outputs.right === Signal.HIGH ? colors.signalColor : colors.noSignalColor;

    pixelMask(g, TILE_SYMBOLS.and, 5, 5, color);
  },
};
